import { useEffect, useMemo, useState } from 'react';

import { IconX } from './icons';
import { ALL_CATEGORIES, CATEGORY_LABELS } from './incidentLabels';
import { useToast } from '../../contexts/ToastContext';
import { hasBackError, toUserErrorMessage } from '../../lib/apiError';
import { formatAllocabId, formatPassenger, formatShortDateTime } from '../../lib/formatters';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { useCreateIncident } from '../../services/api/hooks/useCreateIncident';
import { MIN_AUTOCOMPLETE_LENGTH, useTripAutocomplete } from '../../services/api/hooks/useTripAutocomplete';
import type { ExtranetTripSuggestionOutput, IncidentCategory } from '../../types/extranet-api.types';

interface CreateIncidentModalProps {
  /** Course pré-remplie, quand la modale est ouverte depuis la table des courses. */
  initialCourseId?: string;
  onClose: () => void;
}

const MIN_REPORT = 10;
const MAX_REPORT = 5000;

/**
 * `POST /v1/incidents`. La course se choisit à l'autocomplete
 * (`GET /v1/trips/autocomplete`, v1.13.0) et non plus à la saisie libre : le
 * back cherche sur l'ID Allocab, l'ID externe et le nom du passager, et ne
 * propose que des courses du périmètre de l'appelant. Une course **doit** être
 * sélectionnée pour soumettre — ce qui rend le 422 `COURSE_NOT_FOUND`
 * pratiquement impossible, au lieu de le découvrir après l'envoi.
 */
export const CreateIncidentModal = ({ initialCourseId, onClose }: CreateIncidentModalProps) => {
  const toast = useToast();

  const [selected, setSelected] = useState<ExtranetTripSuggestionOutput | null>(null);
  const [search, setSearch] = useState(initialCourseId ?? '');
  const [clientReport, setClientReport] = useState('');
  const [category, setCategory] = useState<IncidentCategory | ''>('');
  const [touched, setTouched] = useState(false);

  const mutation = useCreateIncident();
  const debouncedSearch = useDebouncedValue(search);
  const suggestionsQuery = useTripAutocomplete(selected ? '' : debouncedSearch);
  // Mémoïsé : sans ça le `?? []` rend un tableau neuf à chaque render et
  // relance l'effet de présélection en boucle.
  const suggestions = useMemo(() => suggestionsQuery.data?.items ?? [], [suggestionsQuery.data]);

  // Ouverture depuis la table des courses : l'id arrive par l'URL, sans le
  // reste de la course. On le résout via l'autocomplete pour présélectionner
  // la bonne ligne et garder le geste en un clic.
  useEffect(() => {
    if (!initialCourseId || selected) return;
    const match = suggestions.find(trip => trip.id === initialCourseId);
    if (match) setSelected(match);
  }, [initialCourseId, selected, suggestions]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const reportError =
    clientReport.length > 0 && (clientReport.length < MIN_REPORT || clientReport.length > MAX_REPORT)
      ? `Remontée client : ${MIN_REPORT} à ${MAX_REPORT} caractères (actuel : ${clientReport.length})`
      : null;

  const courseNotFound = hasBackError(mutation.error, 'COURSE_NOT_FOUND');
  const searching = debouncedSearch.trim().length >= MIN_AUTOCOMPLETE_LENGTH;

  const canSubmit =
    selected !== null &&
    Boolean(category) &&
    clientReport.length >= MIN_REPORT &&
    clientReport.length <= MAX_REPORT &&
    !mutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit || !selected) return;
    mutation.mutate(
      {
        // L'id interne (`Fare_…`) tel que livré par l'autocomplete — pas le
        // `R…` affiché, que le back ne reconnaîtrait pas forcément.
        courseId: selected.id,
        category: category as IncidentCategory,
        clientReport: clientReport.trim(),
      },
      {
        onSuccess: created => {
          toast.success('Réclamation enregistrée', `Rattachée à la course ${formatAllocabId(created.tripKey)}.`);
          onClose();
        },
        onError: err => {
          if (hasBackError(err, 'COURSE_NOT_FOUND')) return;
          const { title, description } = toUserErrorMessage(err, 'Échec de la création');
          toast.error(title, description);
        },
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
      <div
        className="absolute inset-0 bg-black/30 drawer-backdrop-in"
        style={{ backdropFilter: 'blur(2px)' }}
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-incident-title"
        className="relative w-full max-w-[520px] bg-white border border-zinc-200 rounded-md shadow-md drawer-panel-in flex flex-col max-h-[80vh]"
      >
        <header className="sticky top-0 bg-white px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <h2 id="create-incident-title" className="text-base font-semibold text-zinc-900">
            Nouvelle réclamation
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
          >
            <IconX width={16} height={16} />
          </button>
        </header>

        <form
          id="create-incident-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="incident-course" className="text-xs font-medium text-zinc-700">
              Course
            </label>

            {selected ? (
              <SelectedTrip
                trip={selected}
                onChange={() => {
                  setSelected(null);
                  setSearch('');
                }}
              />
            ) : (
              <>
                <input
                  id="incident-course"
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="ID Allocab, ID externe, ou nom du passager"
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={suggestions.length > 0}
                  aria-controls="incident-course-suggestions"
                  className="h-10 w-full px-3 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors"
                />

                {!searching ? (
                  <p className="text-[11px] text-zinc-500">
                    Saisissez au moins {MIN_AUTOCOMPLETE_LENGTH} caractères pour chercher une course.
                  </p>
                ) : suggestionsQuery.isFetching && suggestions.length === 0 ? (
                  <p className="text-[11px] text-zinc-500">Recherche…</p>
                ) : suggestions.length === 0 ? (
                  <p className="text-[11px] text-zinc-500">
                    Aucune course ne correspond sur votre périmètre. Essayez un autre identifiant ou un nom de passager.
                  </p>
                ) : (
                  <ul
                    id="incident-course-suggestions"
                    role="listbox"
                    className="border border-zinc-200 rounded-md max-h-56 overflow-y-auto bg-white divide-y divide-zinc-100"
                  >
                    {suggestions.map(trip => (
                      <li key={trip.id} role="option" aria-selected={false}>
                        <button
                          type="button"
                          onClick={() => setSelected(trip)}
                          className="w-full px-3 py-2 text-left hover:bg-zinc-50 transition-colors flex flex-col gap-0.5"
                        >
                          <TripIdentity trip={trip} />
                          <span className="text-xs text-zinc-500 truncate">
                            {trip.pickupAddress} → {trip.dropoffAddress}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            {courseNotFound ? (
              <p className="text-xs text-red-600">
                Cette course n&apos;est plus accessible sur votre périmètre. Sélectionnez-en une autre.
              </p>
            ) : touched && !selected ? (
              <p className="text-xs text-red-600">Une course doit être sélectionnée.</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="incident-category" className="text-xs font-medium text-zinc-700">
              Type d&apos;incident
            </label>
            <select
              id="incident-category"
              value={category}
              onChange={e => setCategory(e.target.value as IncidentCategory | '')}
              className="h-10 px-2 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors"
            >
              <option value="">— Choisir —</option>
              {ALL_CATEGORIES.map(c => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
            {touched && !category ? <p className="text-xs text-red-600">Un type d&apos;incident est requis.</p> : null}
            <p className="text-[11px] text-zinc-500">La priorité est déterminée par Allocab d&apos;après ce type.</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between">
              <label htmlFor="incident-report" className="text-xs font-medium text-zinc-700">
                Remontée client
              </label>
              <span
                className={['text-xs font-mono tabular-nums', reportError ? 'text-red-600' : 'text-zinc-500'].join(' ')}
              >
                {clientReport.length} / {MAX_REPORT}
              </span>
            </div>
            <textarea
              id="incident-report"
              value={clientReport}
              onChange={e => setClientReport(e.target.value)}
              rows={4}
              placeholder="Verbatim ou résumé de la réclamation transmise par le client."
              className="w-full px-3 py-2 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors resize-y"
            />
            {reportError ? <p className="text-xs text-red-600">{reportError}</p> : null}
          </div>
        </form>

        <footer className="px-6 py-3 border-t border-zinc-100 flex items-center justify-end gap-2 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 text-sm text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="create-incident-form"
            disabled={!canSubmit}
            className="h-9 px-4 text-sm font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {mutation.isPending ? 'Création…' : 'Confirmer'}
          </button>
        </footer>
      </div>
    </div>
  );
};

/** Ligne d'identité d'une course : ID Allocab, ID externe, passager, date. */
const TripIdentity = ({ trip }: { trip: ExtranetTripSuggestionOutput }) => (
  <span className="flex items-center gap-2 flex-wrap text-sm">
    <span className="font-mono tabular-nums text-xs text-zinc-900">{formatAllocabId(trip.id)}</span>
    {trip.externalRef ? (
      <>
        <span className="text-zinc-300">·</span>
        <span className="font-mono tabular-nums text-xs text-zinc-500">{trip.externalRef}</span>
      </>
    ) : null}
    <span className="text-zinc-300">·</span>
    <span className="text-xs text-zinc-700 truncate">{formatPassenger(trip.passenger)}</span>
    <span className="ml-auto text-xs text-zinc-500 font-mono tabular-nums whitespace-nowrap">
      {formatShortDateTime(trip.pickupAt)}
    </span>
  </span>
);

const SelectedTrip = ({ trip, onChange }: { trip: ExtranetTripSuggestionOutput; onChange: () => void }) => (
  <div className="border border-zinc-300 rounded-md px-3 py-2.5 flex flex-col gap-1 bg-zinc-50/60">
    <TripIdentity trip={trip} />
    <span className="text-xs text-zinc-500 truncate">
      {trip.pickupAddress} → {trip.dropoffAddress}
    </span>
    <button
      type="button"
      onClick={onChange}
      className="self-start text-xs text-zinc-600 hover:text-zinc-900 hover:underline underline-offset-2 mt-0.5"
    >
      Changer de course
    </button>
  </div>
);
